import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight, Loader2 } from "lucide-react";
import { MaskedLines, Reveal, FadeIn } from "@/components/Motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { STONES } from "@/data/stones";
import ShowroomMap from "@/components/ShowroomMap";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function Contact() {
  useSeo({
    title: "Contact Varun Marbles | Marble Showrooms in Gurgaon",
    description:
      "Enquire about quartz, marble, granite or onyx. Call +91 97735 58193 or visit our showrooms at Sikanderpur Marble Market and HUDA Marble Market, Sector 33-34, Gurgaon.",
    path: "/contact",
  });
  const [form, setForm] = useState(() => {
    const stone = new URLSearchParams(window.location.search).get("stone");
    const match = STONES.find((s) => s.slug === stone);
    return { name: "", phone: "", email: "", category: match ? match.name : stone || "", message: "" };
  });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        category: form.category || "General Enquiry",
        message: form.message.trim(),
      });
      toast.success("Enquiry sent. We'll reach out shortly.");
      setForm({ name: "", phone: "", email: "", category: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-52 pb-20 md:pb-32">
      <div className="grid lg:grid-cols-12 gap-16 lg:gap-24">
        <div className="lg:col-span-5">
          <FadeIn delay={0.1}>
            <p data-testid="contact-overline" className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-8">
              Contact & enquiries
            </p>
          </FadeIn>
          <h1 data-testid="contact-headline" className="font-serif font-light tracking-tighter leading-[0.98] text-5xl sm:text-6xl">
            <MaskedLines delay={0.2} lines={[<>Tell us about</>, <><span className="italic">your space.</span></>]} />
          </h1>
          <FadeIn delay={0.7}>
            <p className="mt-8 max-w-sm text-base font-light leading-relaxed text-muted-foreground">
              Share a few details and our team will call you back with availability, pricing and
              slab recommendations.
            </p>
            <ul className="mt-12 space-y-6 text-sm font-light">
              <li>
                <a href="tel:+919773558193" data-testid="contact-phone" className="flex items-center gap-4 hover:text-foreground transition-colors duration-300">
                  <Phone className="w-4 h-4 text-secondary-foreground" /> +91 97735 58193
                </a>
              </li>
              <li>
                <a href="mailto:enquiries@varunmarbles.in" data-testid="contact-email" className="flex items-center gap-4 hover:text-foreground transition-colors duration-300">
                  <Mail className="w-4 h-4 text-secondary-foreground" /> enquiries@varunmarbles.in
                </a>
              </li>
            </ul>
          </FadeIn>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.3}>
            <form onSubmit={submit} data-testid="enquiry-form" className="space-y-10 border border-border bg-card/60 p-6 sm:p-8 md:p-14">
              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="enquiry-name" className="block text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-3">
                    Name *
                  </label>
                  <Input
                    id="enquiry-name"
                    data-testid="enquiry-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                    className="rounded-none border-0 border-b border-input bg-transparent px-0 py-3 text-base font-light focus-visible:ring-0 focus-visible:border-foreground placeholder:text-muted-foreground/50"
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-phone" className="block text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-3">
                    Phone *
                  </label>
                  <Input
                    id="enquiry-phone"
                    data-testid="enquiry-phone-input"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91 ..."
                    className="rounded-none border-0 border-b border-input bg-transparent px-0 py-3 text-base font-light focus-visible:ring-0 focus-visible:border-foreground placeholder:text-muted-foreground/50"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="enquiry-email" className="block text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-3">
                    Email
                  </label>
                  <Input
                    id="enquiry-email"
                    type="email"
                    data-testid="enquiry-email-input"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className="rounded-none border-0 border-b border-input bg-transparent px-0 py-3 text-base font-light focus-visible:ring-0 focus-visible:border-foreground placeholder:text-muted-foreground/50"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-3">
                    Interested in
                  </label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
                  >
                    <SelectTrigger
                      data-testid="enquiry-category-select"
                      className="rounded-none border-0 border-b border-input bg-transparent px-0 py-3 text-base font-light focus:ring-0"
                    >
                      <span className={form.category ? "" : "text-muted-foreground/50"}>
                        {form.category || "Select a stone"}
                      </span>
                    </SelectTrigger>
                    <SelectContent data-testid="enquiry-category-options">
                      <SelectItem value="General Enquiry">General Enquiry</SelectItem>
                      {STONES.map((s) => (
                        <SelectItem key={s.slug} value={s.name} data-testid={`enquiry-option-${s.slug}`}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label htmlFor="enquiry-message" className="block text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-3">
                  Your project
                </label>
                <Textarea
                  id="enquiry-message"
                  data-testid="enquiry-message-input"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="Kitchen counters, flooring, a statement wall — tell us what you're dreaming of."
                  className="rounded-none border-0 border-b border-input bg-transparent px-0 py-3 text-base font-light focus-visible:ring-0 focus-visible:border-foreground placeholder:text-muted-foreground/50 resize-none"
                />
              </div>

              <Button
                type="submit"
                data-testid="enquiry-submit-btn"
                disabled={sending}
                className="group rounded-none bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase px-10 py-6 transition-colors duration-300 hover:bg-foreground/80"
              >
                {sending ? (
                  <>
                    Sending <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  </>
                ) : (
                  <>
                    Send enquiry
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>
          </Reveal>
        </div>
      </div>
      </section>

      <section data-testid="showroom-map-section" className="max-w-7xl mx-auto px-6 md:px-12 pb-28 md:pb-40">
        <ShowroomMap />
      </section>
    </>
  );
}
